import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

import { Page, PagesResponse } from './_types';
import { denormalizePage, normalizePage } from './normalizers';

interface FetchPagesOptions {
  page?: number;
  perPage?: number;
  search?: string;
}

interface FetchPagesResponse {
  pages: Page[];
  total: number;
}

/**
 * Fetch pages with optional pagination and search
 */
export const fetchPages = async ({
  page = 1,
  perPage = 10,
  search,
}: FetchPagesOptions = {}): Promise<FetchPagesResponse> => {
  const params = {
    page,
    per: perPage,
    ...(search && { 'q[name_cont]': search }),
  };

  const response = await api.get('/api/v4/pages', { params });
  const data = response.data as PagesResponse;
  const pages = data.page ? data.page.map((page) => normalizePage(page)) : [];

  return {
    pages,
    total: data.meta.total,
  };
};

/**
 * Fetch a single page by ID
 */
export const fetchPage = async (id: Page['id']): Promise<Page> => {
  const response = await api.get(`/api/v4/pages/${id}`);
  return normalizePage(response.data);
};

/**
 * Create a new page
 */
export const createPage = async (page: Partial<Page>): Promise<Page> => {
  const data = denormalizePage(page);
  const response = await api.post('/api/v4/pages', { page: data });
  return normalizePage(response.data);
};

/**
 * Update an existing page
 */
export const updatePage = async (
  id: Identifier,
  page: Partial<Page>
): Promise<Page> => {
  const data = denormalizePage(page);
  const response = await api.put(`/api/v4/pages/${id}`, { page: data });
  return normalizePage(response.data);
};

/**
 * Delete a page
 */
export const deletePage = async (id: Identifier): Promise<Page> => {
  const response = await api.delete(`/api/v4/pages/${id}`);
  return normalizePage(response.data);
};

/**
 * Generate HTML preview for a page
 */
export const previewPage = async (
  id: Identifier,
  content: string,
  platformId?: string | number
): Promise<string> => {
  const response = await api.post(`/api/v4/pages/${id}/preview`, {
    content,
    platform_id: platformId,
  });
  return response.data?.html || '';
};
